import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const companyInfo = {
  phone: "(819) 775-2130",
  phone2: "(647) 701-4224",
  email: "info@djengineeringroup.ca",
  address: "Gatineau / Ottawa",
};

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 10000);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError(language === 'fr' 
        ? 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.'
        : 'An error occurred. Please try again or contact us directly by phone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    {
      icon: Phone,
      title: t('contactPage.details.phone'),
      content: companyInfo.phone,
      href: `tel:${companyInfo.phone}`,
    },
    {
      icon: Phone,
      title: t('contactPage.details.phone'),
      content: companyInfo.phone2,
      href: `tel:${companyInfo.phone2}`,
    },
    {
      icon: Mail,
      title: t('contactPage.details.email'),
      content: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
    },
    {
      icon: MapPin,
      title: t('contactPage.details.location'),
      content: companyInfo.address,
      href: null,
    },
    {
      icon: Clock,
      title: t('contactPage.details.hours'),
      content: t('contactPage.details.hoursValue'),
      href: null,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 70%, #0d9488 0%, transparent 50%)`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            {t('contactPage.badge')}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('contactPage.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {t('contactPage.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('contactPage.infoTitle')}</h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                {t('contactPage.infoDesc')}
              </p>
              
              <div className="space-y-6">
                {contactDetails.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 mb-1">{item.title}</p>
                      {item.href ? (
                        <a href={item.href} className="text-slate-600 hover:text-blue-700 transition-colors">
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-slate-600">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-0 shadow-xl">
                <CardContent className="p-8 lg:p-10">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-blue-700" />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">{t('contactPage.success.title')}</h3>
                      <p className="text-slate-600">
                        {t('contactPage.success.desc')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('contactPage.formTitle')}</h2>
                      <p className="text-slate-600 mb-8">{t('contactPage.formDesc')}</p>
                      
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-slate-700">{t('contactPage.labels.name')} *</Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                              placeholder={t('contactPage.placeholders.name')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">{t('contactPage.labels.email')} *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                              placeholder={t('contactPage.placeholders.email')}
                            />
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-slate-700">{t('contactPage.labels.phone')}</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                              placeholder={t('contactPage.placeholders.phone')}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subject" className="text-slate-700">{t('contactPage.labels.subject')} *</Label>
                            <Input
                              id="subject"
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              required
                              className="border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                              placeholder={t('contactPage.placeholders.subject')}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-slate-700">{t('contactPage.labels.message')} *</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            rows={6}
                            className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                            placeholder={t('contactPage.placeholders.message')}
                          />
                        </div>
                        
                        {error && (
                          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm">{error}</p>
                          </div>
                        )}
                        
                        <Button 
                          type="submit" 
                          disabled={isSubmitting}
                          className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg rounded-xl font-medium transition-all duration-300 hover:shadow-xl hover:shadow-blue-700/25 group disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                              {language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                            </>
                          ) : (
                            <>
                              {t('contactPage.submit')}
                              <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
